import { getCookie } from "./token";
import i18n from "../i18n";
import { resolveFileUrl } from "./fileUrl";

/**
 * Triggers a browser download for an already fetched blob.
 */
export function saveBlobAs(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Fetches a file with authentication and opens it in a new tab.
 * Use this instead of window.open() or <a href> for protected endpoints.
 */
export async function openAuthenticatedFile(fileUrl: string): Promise<void> {
  const accessToken = getCookie("accessToken");
  const resolved = resolveFileUrl(fileUrl) ?? fileUrl;
  const res = await fetch(resolved, {
    credentials: "include",
    headers: {
      'Accept-Language': i18n.language || 'he',
      'X-App-Name': 'admin',
      ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch file: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const newTab = window.open(url, "_blank");
  if (newTab) {
    newTab.onload = () => URL.revokeObjectURL(url);
  } else {
    URL.revokeObjectURL(url);
  }
}

/**
 * Fetches a file with authentication and triggers a download.
 * Use this instead of window.open() or <a href> for protected endpoints.
 */
export async function downloadAuthenticatedFile(
  fileUrl: string,
  fileName: string
): Promise<void> {
  const accessToken = getCookie("accessToken");
  const resolved = resolveFileUrl(fileUrl) ?? fileUrl;
  const res = await fetch(resolved, {
    credentials: "include",
    headers: {
      'Accept-Language': i18n.language || 'he',
      'X-App-Name': 'admin',
      ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch file: ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
